import { useState } from 'react';

import type * as api from '@gitbook/api-client';
import {
    Description,
    Dialog,
    DialogBody,
    DialogHeader,
    DialogRoot,
    Heading,
    useOverlayTriggerState,
} from '@gitbook/spine-react';

import { SpaceCreationDialog } from '../../Space';
import { CreateSiteVariantForm, type CreateSiteVariantFormProps } from './CreateSiteVariantForm';
import { SpaceEmptyState } from './SpaceEmptyState';
import { type UseAvailableSiteSpacesProps, useAvailableSiteSpaces } from './useAvailableSiteSpaces';

export interface CreateSiteVariantDialogProps
    extends UseAvailableSiteSpacesProps,
        Omit<
            CreateSiteVariantFormProps,
            'spaces' | 'createSpaceDialogTrigger' | 'defaultSpaceId'
        > {}

export function CreateSiteVariantDialog(props: CreateSiteVariantDialogProps) {
    return (
        <Dialog>
            <CreateSiteVariantDialogContent {...props} />
        </Dialog>
    );
}

function CreateSiteVariantDialogContent(props: CreateSiteVariantDialogProps) {
    const { organization } = props;
    const spaces = useAvailableSiteSpaces(props);

    const createSpaceDialogTrigger = useOverlayTriggerState();
    const [createdSpace, setCreatedSpace] = useState<api.Space | null>(null);

    return (
        <>
            <DialogRoot {...createSpaceDialogTrigger.rootProps}>
                <SpaceCreationDialog
                    organizationId={organization.id}
                    target={{
                        type: 'siteVariant',
                        props: {
                            site: props.site,
                            siteSection: props.siteSection,
                            onCreateSpace: setCreatedSpace,
                        },
                    }}
                />
            </DialogRoot>
            <DialogHeader>
                <Heading>Add variant</Heading>
                <Description>
                    A variant is a way to create different versions of your site content. Use
                    variants to add translations or any alternative versions of your site.
                </Description>
            </DialogHeader>
            {spaces.length > 0 || props.siteSection ? (
                <CreateSiteVariantForm
                    {...props}
                    defaultSpaceId={createdSpace?.id ?? null}
                    spaces={spaces}
                />
            ) : (
                <DialogBody>
                    <SpaceEmptyState
                        className="border"
                        organization={organization}
                        createSpaceDialogTrigger={createSpaceDialogTrigger}
                        description={
                            <>
                                To add a variant, you need to create a space first. Spaces are where
                                your content lives.
                            </>
                        }
                    />
                </DialogBody>
            )}
        </>
    );
}
